import React, {
  createContext,
  useContext,
  useRef,
  useEffect,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import { dropUserCart } from '../../../../../utilities/api'
import { Spinner } from '../../../../LoadingContainer/Spinner'

const TimeoutContext = createContext(undefined)

export const useTimeout = () => {
  const context = useContext(TimeoutContext)
  if (!context) {
    throw new Error('useTimeout must be used within a TimeoutProvider')
  }
  return context
}

export const TimeoutProvider = ({ children }) => {
  const timerRef = useRef(null)
  const timeoutRef = useRef(null)
  const navigate = useNavigate()
  const [eventUUID, setEventUUID] = useState(undefined)
  const [isTimeout, setIsTimeout] = useState(false)
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const [exitError, setExitError] = useState('')
  const isExitingRef = useRef(false)

  const startTimer = (remainingTime, eventUUID = undefined) => {
    if (eventUUID) {
      setEventUUID(eventUUID)
    }

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    // Set the timeout
    timeoutRef.current = remainingTime

    if (remainingTime <= 0) {
      setIsTimeout(true)
      return
    }

    timerRef.current = setTimeout(() => {
      setIsTimeout(true)
    }, remainingTime)
  }

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const exitCheckout = async () => {
    if (isExitingRef.current) return
    isExitingRef.current = true
    setIsExiting(true)
    setExitError('')
    // The event page refetches inventory on mount. Stay here when the drop
    // fails, or those seats are still held when it reloads.
    try {
      await dropUserCart({ eventUUID })
    } catch (error) {
      console.error(error)
      isExitingRef.current = false
      setIsExiting(false)
      setExitError('We could not release your tickets. Please try again.')
      return
    }
    sessionStorage.removeItem('cart')
    navigate(-1)
  }

  const handleCartExpired = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    sessionStorage.removeItem('cart')
    setIsTimeout(true)
  }

  // Timeout is only shown IF not purchasing + timeout
  const TimeoutDialog = () => (
    <Modal
      show={isTimeout && !isPurchasing}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header>
        <Modal.Title>Session Expired</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {exitError ||
          'Your session has expired. The items in your cart have been released.'}
      </Modal.Body>
      <Modal.Footer>
        <Button
          size="lg"
          variant="primary"
          className="d-flex align-items-center justify-content-center"
          onClick={exitCheckout}
          disabled={isExiting}
        >
          {isExiting ? <Spinner /> : 'Exit'}
        </Button>
      </Modal.Footer>
    </Modal>
  )

  return (
    <TimeoutContext.Provider
      value={{
        startTimer,
        isPurchasing,
        setIsPurchasing,
        handleCartExpired,
      }}
    >
      {children}
      <TimeoutDialog />
    </TimeoutContext.Provider>
  )
}
